import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { useAppTheme } from "@hooks/useAppTheme";
/* -----------------Types--------------- */
import { getSortedChoices } from "./helper";

import { events } from "@constants/tracking";
/* -----------------Icons--------------- */
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import { TTSContainer } from "@memorang/features/src/TTS";
import type { Choice } from "@memorang/types/src/session/new-session";
/* -----------------Components--------------- */
import { Box } from "@memorang/ui";
import { trackCustomEvent } from "analytics";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";

/* -----------------Props--------------- */
type Props = {
	choices: Choice[];
	itemId: number;
	activeChoiceId?: string;
};

const RenderExplanation = ({
	explanation,
	fontSize,
}: {
	explanation: string | undefined;
	fontSize: number;
}) => {
	if (explanation) {
		const normalizedExplanation = explanation.replace(/<br>/g, "\n\n");
		return (
			<CustomMarkdownRenderer
				text={normalizedExplanation}
				variant="explanation"
				fontSize={fontSize}
				paragraphMarginVertical={6}
			/>
		);
	}
	return null;
};

/* -----------------Component--------------- */
const HumanExplanation = (props: Props) => {
	const { choices, itemId, activeChoiceId } = props;
	const { t } = useTranslation();

	const previewChoice = activeChoiceId
		? choices.find((choice) => choice.id === activeChoiceId)
		: undefined;
	const sortedChoices = previewChoice
		? [previewChoice]
		: getSortedChoices(choices);
	const correctChoices = previewChoice
		? [previewChoice]
		: sortedChoices.filter(({ isCorrect }) => isCorrect);

	const hasChoicesWithText = choices.some(({ stem }) => stem);

	const theme = useAppTheme();

	const fontSize = useAppStore((store) => store.app.fontSizes?.stem) || 16;

	const RenderChoice = ({ isCorrect, stem }: Choice) => (
		<Box display="flex" flexDirection="row" gap={8} alignItems="center">
			{isCorrect ? (
				<Icon
					name="checkbox-marked-circle"
					color={theme.colors.progress.high}
					size={24}
				/>
			) : (
				<Icon name="close-circle" size={24} color={theme.colors.progress.low} />
			)}
			<Box flex={1}>
				<CustomMarkdownRenderer
					text={stem}
					customStyles={{
						paragraph: {
							...theme.typography.fonts.medium,
						},
					}}
					fontSize={14}
					variant="answer"
				/>
			</Box>
		</Box>
	);
	const ChoicesWithExplanation = () => (
		<Box gap={8}>
			{sortedChoices.map((choice) => (
				<Fragment key={choice.id}>
					{hasChoicesWithText ? <RenderChoice {...choice} /> : null}
					<RenderExplanation
						explanation={
							i18n.language === "zh-CN" && choice.explanation_mandarin
								? choice?.explanation_mandarin
								: (choice?.explanation ?? "")
						}
						fontSize={fontSize}
					/>
				</Fragment>
			))}
		</Box>
	);

	const explanation =
		i18n.language === "zh-CN" && correctChoices[0].explanation_mandarin
			? correctChoices[0]?.explanation_mandarin
			: (correctChoices[0]?.explanation ?? "");

	if (correctChoices.length > 1) {
		return (
			<Box gap={16}>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
				>
					<CustomListSubheaderContainer enableGutters={false}>
						{t("session.explanation.title")}
					</CustomListSubheaderContainer>
					<TTSContainer
						itemId={`${itemId}`}
						eventName={events.ttsClicked}
						trackCustomEvent={trackCustomEvent}
						explanation={explanation}
						size={18}
						allowPrefetch={true}
					/>
				</Box>

				<Box gap={8}>
					{correctChoices.map((choice) => (
						<Box key={choice.id}>
							<RenderChoice {...choice} />
						</Box>
					))}
					<RenderExplanation explanation={explanation} fontSize={fontSize} />
				</Box>
			</Box>
		);
	}
	return (
		<Box>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
			>
				<CustomListSubheaderContainer
					enableGutters={false}
					textVariant="titleMedium"
					style={{
						paddingVertical: 16,
					}}
				>
					{t("session.explanation.title")}
				</CustomListSubheaderContainer>
				<TTSContainer
					itemId={`${itemId}`}
					explanation={explanation}
					size={18}
					allowPrefetch={true}
					eventName={events.ttsClicked}
					trackCustomEvent={trackCustomEvent}
				/>
			</Box>
			<ChoicesWithExplanation />
		</Box>
	);
};

export default HumanExplanation;
